import { useEffect, useMemo, useState } from "react";
import {
	collection,
	onSnapshot,
	orderBy,
	query,
	serverTimestamp,
	updateDoc,
	doc,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, appId, storage } from "@/lib/firebase";
import { ChevronLeft, ChevronRight, ExternalLink, FileText, RefreshCw, Search } from "lucide-react";

type WebsiteServiceRequestType =
	| "new_website"
	| "website_enhancement"
	| "redesign_and_enhancement";
type WebsiteServiceAdminStatus = "new" | "in_review" | "contacted" | "closed";

interface WebsiteServiceRequestRecord {
	id: string;
	companyName: string;
	contactName: string;
	contactEmail: string;
	contactPhone: string;
	websiteUrl?: string | null;
	requestType: WebsiteServiceRequestType;
	briefRequest?: string | null;
	source: string;
	adminStatus: WebsiteServiceAdminStatus;
	adminNotes?: string;
	capabilityStatement?: {
		fileName: string;
		fileSize: number;
		fileType: string;
		storagePath: string;
	} | null;
	createdAt?: { toDate?: () => Date };
	updatedAt?: { toDate?: () => Date };
}

interface AdminWebsiteServiceRequestsProps {
	showNotify?: (message: string, type?: "success" | "error" | "info") => void;
}

const requestTypeLabel: Record<WebsiteServiceRequestType, string> = {
	new_website: "New Website",
	website_enhancement: "Enhancement",
	redesign_and_enhancement: "Redesign + Enhancement",
};

const statusLabel: Record<WebsiteServiceAdminStatus, string> = {
	new: "New",
	in_review: "In Review",
	contacted: "Contacted",
	closed: "Closed",
};

const statusColorClass: Record<WebsiteServiceAdminStatus, string> = {
	new: "bg-blue-100 text-blue-700",
	in_review: "bg-amber-100 text-amber-700",
	contacted: "bg-purple-100 text-purple-700",
	closed: "bg-emerald-100 text-emerald-700",
};

export const AdminWebsiteServiceRequests = ({
	showNotify,
}: AdminWebsiteServiceRequestsProps): JSX.Element => {
	const [requests, setRequests] = useState<WebsiteServiceRequestRecord[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
	const [statusFilter, setStatusFilter] = useState<WebsiteServiceAdminStatus | "all">("all");
	const [search, setSearch] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);
	const [adminNotesDraft, setAdminNotesDraft] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	useEffect(() => {
		const requestsQuery = query(
			collection(db, "artifacts", appId, "public", "data", "websiteServiceRequests"),
			orderBy("createdAt", "desc")
		);
		const unsubscribe = onSnapshot(
			requestsQuery,
			(snapshot) => {
				const records = snapshot.docs.map((snapshotDoc) => {
					return {
						id: snapshotDoc.id,
						...snapshotDoc.data(),
					} as WebsiteServiceRequestRecord;
				});
				setRequests(records);
				setLoading(false);
			},
			(error) => {
				console.error(
					"[AdminWebsiteServiceRequests] Failed to subscribe to website service requests. Admin list cannot refresh in realtime:",
					error
				);
				showNotify?.("Failed to load website service requests.", "error");
				setLoading(false);
			}
		);
		return unsubscribe;
	}, [showNotify]);

	const filteredRequests = useMemo(() => {
		const normalizedSearch = search.trim().toLowerCase();
		return requests.filter((record) => {
			const passesStatusFilter =
				statusFilter === "all" ? true : record.adminStatus === statusFilter;
			if (!passesStatusFilter) return false;
			if (!normalizedSearch) return true;
			const searchBlob = [
				record.companyName,
				record.contactName,
				record.contactEmail,
				record.contactPhone,
				record.websiteUrl ?? "",
				record.briefRequest ?? "",
			]
				.join(" ")
				.toLowerCase();
			return searchBlob.includes(normalizedSearch);
		});
	}, [requests, search, statusFilter]);

	// Reset to first page when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [search, statusFilter]);

	const totalPages = Math.max(1, Math.ceil(filteredRequests.length / pageSize));
	const paginatedRequests = useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		return filteredRequests.slice(start, start + pageSize);
	}, [filteredRequests, currentPage, pageSize]);

	const selectedRequest = useMemo(
		() =>
			filteredRequests.find((record) => record.id === selectedRequestId) ??
			paginatedRequests[0] ??
			null,
		[filteredRequests, selectedRequestId, paginatedRequests]
	);

	useEffect(() => {
		if (!selectedRequest) {
			setAdminNotesDraft("");
			return;
		}
		setAdminNotesDraft(selectedRequest.adminNotes ?? "");
	}, [selectedRequest?.id]);

	const formatDate = (timestamp: WebsiteServiceRequestRecord["createdAt"]) => {
		if (!timestamp || !timestamp.toDate) return "Unknown";
		return timestamp.toDate().toLocaleString();
	};

	const formatBytes = (bytes: number) => {
		if (!Number.isFinite(bytes) || bytes <= 0) return "Unknown size";
		const units = ["B", "KB", "MB", "GB"];
		let value = bytes;
		let unitIndex = 0;
		while (value >= 1024 && unitIndex < units.length - 1) {
			value /= 1024;
			unitIndex += 1;
		}
		return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
	};

	const handleStatusUpdate = async (nextStatus: WebsiteServiceAdminStatus) => {
		if (!selectedRequest) return;
		setIsUpdating(true);
		try {
			await updateDoc(
				doc(
					db,
					"artifacts",
					appId,
					"public",
					"data",
					"websiteServiceRequests",
					selectedRequest.id
				),
				{
					adminStatus: nextStatus,
					adminNotes: adminNotesDraft.trim(),
					updatedAt: serverTimestamp(),
				}
			);
			showNotify?.(
				`Request for ${selectedRequest.companyName} moved to ${statusLabel[nextStatus]}.`,
				"success"
			);
		} catch (error) {
			console.error(
				`[AdminWebsiteServiceRequests] Failed to update request "${selectedRequest.id}" to status "${nextStatus}". Request will remain unchanged:`,
				error
			);
			showNotify?.("Unable to update request status. Please try again.", "error");
		} finally {
			setIsUpdating(false);
		}
	};

	const handleOpenCapabilityStatement = async (record: WebsiteServiceRequestRecord) => {
		if (!record.capabilityStatement) {
			showNotify?.("No capability statement uploaded for this request.", "info");
			return;
		}
		try {
			const statementStorageRef = ref(storage, record.capabilityStatement.storagePath);
			const downloadUrl = await getDownloadURL(statementStorageRef);
			window.open(downloadUrl, "_blank", "noopener,noreferrer");
		} catch (error) {
			console.error(
				`[AdminWebsiteServiceRequests] Failed to generate capability statement download URL for request "${record.id}". File cannot be opened:`,
				error
			);
			showNotify?.("Unable to open capability statement. Check storage access.", "error");
		}
	};

	if (loading) {
		return <div className="text-gray-600">Loading website service requests...</div>;
	}

	return (
		<div className="space-y-5">
			<div>
				<h2 className="text-xl font-bold text-gray-900">Website Service Requests</h2>
				<p className="text-sm text-gray-600 mt-1">
					Monitor website development and enhancement inquiries submitted from the
					footer CTA.
				</p>
			</div>

			<div className="rounded-xl border border-gray-200 bg-white p-4">
				<div className="grid grid-cols-1 md:grid-cols-4 gap-3">
					<div className="md:col-span-2 relative">
						<Search
							size={16}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
						/>
						<input
							type="text"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder="Search by company, contact, email..."
							className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>
					<select
						value={statusFilter}
						onChange={(event) =>
							setStatusFilter(event.target.value as WebsiteServiceAdminStatus | "all")
						}
						className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
					>
						<option value="all">All statuses</option>
						<option value="new">New</option>
						<option value="in_review">In Review</option>
						<option value="contacted">Contacted</option>
						<option value="closed">Closed</option>
					</select>
					<div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
						<span className="font-semibold">{filteredRequests.length}</span> request(s)
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.2fr),minmax(0,1fr)] gap-4">
				<div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
										Company
									</th>
									<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
										Type
									</th>
									<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
										Status
									</th>
									<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
										Submitted
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-100">
								{filteredRequests.length === 0 ? (
									<tr>
										<td colSpan={4} className="px-4 py-8 text-sm text-center text-gray-500">
											No website service requests found.
										</td>
									</tr>
								) : (
									paginatedRequests.map((record) => {
										const active =
											(selectedRequest?.id ?? selectedRequestId ?? paginatedRequests[0]?.id) ===
											record.id;
										return (
											<tr
												key={record.id}
												onClick={() => setSelectedRequestId(record.id)}
												className={`cursor-pointer transition-colors ${
													active ? "bg-blue-50/70" : "hover:bg-gray-50"
												}`}
											>
												<td className="px-4 py-3 align-top">
													<p className="text-sm font-semibold text-gray-900">
														{record.companyName}
													</p>
													<p className="text-xs text-gray-600">{record.contactEmail}</p>
												</td>
												<td className="px-4 py-3 text-sm text-gray-700 align-top">
													{requestTypeLabel[record.requestType]}
												</td>
												<td className="px-4 py-3 align-top">
													<span
														className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusColorClass[record.adminStatus]}`}
													>
														{statusLabel[record.adminStatus]}
													</span>
												</td>
												<td className="px-4 py-3 text-xs text-gray-600 align-top">
													{formatDate(record.createdAt)}
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>

				<div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
					{selectedRequest ? (
						<>
							<div>
								<h3 className="text-lg font-bold text-gray-900">
									{selectedRequest.companyName}
								</h3>
								<p className="text-sm text-gray-600">
									{selectedRequest.contactName} · {selectedRequest.contactEmail}
								</p>
								<p className="text-sm text-gray-600">{selectedRequest.contactPhone}</p>
							</div>

							<div className="grid grid-cols-1 gap-3 text-sm">
								<div>
									<p className="font-semibold text-gray-900">Request type</p>
									<p className="text-gray-700">{requestTypeLabel[selectedRequest.requestType]}</p>
								</div>
								<div>
									<p className="font-semibold text-gray-900">Website</p>
									<p className="text-gray-700">
										{selectedRequest.websiteUrl ? (
											<a
												className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
												href={selectedRequest.websiteUrl}
												target="_blank"
												rel="noopener noreferrer"
											>
												{selectedRequest.websiteUrl}
												<ExternalLink size={13} />
											</a>
										) : (
											"No website provided"
										)}
									</p>
								</div>
								<div>
									<p className="font-semibold text-gray-900">Initial request notes</p>
									<p className="text-gray-700 whitespace-pre-wrap">
										{selectedRequest.briefRequest?.trim() || "No notes provided."}
									</p>
								</div>
							</div>

							<div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-3">
								<div className="flex items-center justify-between gap-3">
									<div>
										<p className="text-sm font-semibold text-blue-900">
											Capability statement
										</p>
										<p className="text-xs text-blue-800">
											{selectedRequest.capabilityStatement
												? `${selectedRequest.capabilityStatement.fileName} (${formatBytes(selectedRequest.capabilityStatement.fileSize)})`
												: "Not uploaded"}
										</p>
									</div>
									<button
										type="button"
										onClick={() => handleOpenCapabilityStatement(selectedRequest)}
										disabled={!selectedRequest.capabilityStatement}
										className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
									>
										<FileText size={14} />
										Open PDF
									</button>
								</div>
							</div>

							<div className="space-y-2">
								<label className="block text-sm font-semibold text-gray-900">Admin notes</label>
								<textarea
									rows={4}
									value={adminNotesDraft}
									onChange={(event) => setAdminNotesDraft(event.target.value)}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
									placeholder="Capture follow-ups, scope notes, or meeting outcomes."
								/>
							</div>

							<div className="grid grid-cols-2 gap-2">
								<button
									type="button"
									disabled={isUpdating}
									onClick={() => handleStatusUpdate("in_review")}
									className="inline-flex items-center justify-center gap-1 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
								>
									<RefreshCw size={14} />
									Mark In Review
								</button>
								<button
									type="button"
									disabled={isUpdating}
									onClick={() => handleStatusUpdate("contacted")}
									className="rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
								>
									Mark Contacted
								</button>
								<button
									type="button"
									disabled={isUpdating}
									onClick={() => handleStatusUpdate("new")}
									className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
								>
									Reset to New
								</button>
								<button
									type="button"
									disabled={isUpdating}
									onClick={() => handleStatusUpdate("closed")}
									className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
								>
									Close Request
								</button>
							</div>
						</>
					) : (
						<p className="text-sm text-gray-500">
							Select a request to view details and update its status.
						</p>
					)}
				</div>
			</div>

			{filteredRequests.length > 0 && (
				<div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
					<div className="flex items-center gap-4">
						<span className="text-sm text-gray-600">
							Showing{" "}
							<span className="font-medium text-gray-900">
								{(currentPage - 1) * pageSize + 1}–
								{Math.min(currentPage * pageSize, filteredRequests.length)}
							</span>{" "}
							of <span className="font-medium text-gray-900">{filteredRequests.length}</span>{" "}
							request{filteredRequests.length !== 1 ? "s" : ""}
						</span>
						<label className="flex items-center gap-2 text-sm text-gray-600">
							Rows per page
							<select
								value={pageSize}
								onChange={(e) => {
									setPageSize(Number(e.target.value));
									setCurrentPage(1);
								}}
								className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
							>
								<option value={10}>10</option>
								<option value={25}>25</option>
								<option value={50}>50</option>
								<option value={100}>100</option>
							</select>
						</label>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
							disabled={currentPage <= 1}
							className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
						>
							<ChevronLeft size={16} />
							Previous
						</button>
						<span className="text-sm text-gray-600">
							Page <span className="font-medium text-gray-900">{currentPage}</span> of{" "}
							<span className="font-medium text-gray-900">{totalPages}</span>
						</span>
						<button
							type="button"
							onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
							disabled={currentPage >= totalPages}
							className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
						>
							Next
							<ChevronRight size={16} />
						</button>
					</div>
				</div>
			)}
		</div>
	);
};
